import { useState, useEffect } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Mail, Loader2, ArrowRight } from "lucide-react";
import { CONTACT } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ContactDialog({ open, onOpenChange, prefill }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    interest: "research",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Apply prefill on open
  useEffect(() => {
    if (open && prefill) {
      setForm((f) => ({ ...f, ...prefill }));
    }
  }, [open, prefill]);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email, and message");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Message received — we'll be in touch");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not send message";
      toast.error(typeof detail === "string" ? detail : "Could not send message");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setDone(false);
    setForm({
      name: "",
      email: "",
      organization: "",
      interest: "research",
      message: "",
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) setTimeout(reset, 300);
      }}
    >
      <DialogContent
        data-testid={CONTACT.dialog}
        className="bg-[#07111F] border border-white/10 text-white max-w-xl"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-[22px] font-medium tracking-tight">
            {done ? "Message received." : "Partner with Neurobolt"}
          </DialogTitle>
          <DialogDescription className="text-[#94A3B8]">
            {done ? (
              <>
                Thanks — we'll be in touch from{" "}
                <a
                  className="text-[#38BDF8] hover:underline"
                  href="mailto:contact@neurobolt.ai"
                >
                  contact@neurobolt.ai
                </a>
                .
              </>
            ) : (
              <>
                Tell us how you'd like to engage. For direct email:{" "}
                <a
                  className="text-[#38BDF8] hover:underline"
                  href="mailto:contact@neurobolt.ai"
                >
                  contact@neurobolt.ai
                </a>
                .
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div data-testid={CONTACT.success} className="mt-2 space-y-4">
            <div className="rounded-xl border border-[#38BDF8]/30 bg-[#38BDF8]/5 p-4 text-[14px] text-[#CBD5E1]">
              Your message has been logged. A team member will review and reply soon.
            </div>
            <button
              onClick={() => onOpenChange(false)}
              className="w-full inline-flex justify-center items-center gap-2 px-5 py-3 rounded-full bg-white text-[#020617] text-[14px] font-semibold hover:bg-[#BFDBFE]"
            >
              Close <ArrowRight size={14} />
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-2 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Name">
                <Input
                  data-testid={CONTACT.name}
                  required
                  value={form.name}
                  onChange={update("name")}
                  className="bg-[#0B1426] border-white/10 focus:border-[#38BDF8] text-white"
                  placeholder="Jane Doe"
                />
              </Field>
              <Field label="Email">
                <Input
                  data-testid={CONTACT.email}
                  type="email"
                  required
                  value={form.email}
                  onChange={update("email")}
                  className="bg-[#0B1426] border-white/10 focus:border-[#38BDF8] text-white"
                  placeholder="you@org.com"
                />
              </Field>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Organization (optional)">
                <Input
                  data-testid={CONTACT.org}
                  value={form.organization}
                  onChange={update("organization")}
                  className="bg-[#0B1426] border-white/10 focus:border-[#38BDF8] text-white"
                  placeholder="Institute / Company"
                />
              </Field>
              <Field label="Interest">
                <Select
                  value={form.interest}
                  onValueChange={(v) => setForm((f) => ({ ...f, interest: v }))}
                >
                  <SelectTrigger
                    data-testid={CONTACT.interest}
                    className="bg-[#0B1426] border-white/10 focus:ring-[#38BDF8]/40 text-white"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#07111F] border-white/10 text-white">
                    <SelectItem value="research">Research / Academic</SelectItem>
                    <SelectItem value="clinical">Clinical</SelectItem>
                    <SelectItem value="investment">Investment</SelectItem>
                    <SelectItem value="technical">Technical</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <Field label="Message">
              <Textarea
                data-testid={CONTACT.message}
                required
                rows={5}
                value={form.message}
                onChange={update("message")}
                className="bg-[#0B1426] border-white/10 focus:border-[#38BDF8] text-white resize-none"
                placeholder="Tell us a bit about your work and how you'd like to engage..."
              />
            </Field>

            <button
              data-testid={CONTACT.submit}
              type="submit"
              disabled={submitting}
              className="btn-glow w-full inline-flex justify-center items-center gap-2 px-5 py-3 rounded-full bg-white text-[#020617] text-[14px] font-semibold hover:bg-[#BFDBFE] disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Sending…
                </>
              ) : (
                <>
                  Send message <ArrowRight size={14} />
                </>
              )}
            </button>

            <a
              href="mailto:contact@neurobolt.ai"
              className="block text-center text-[12.5px] text-[#64748B] hover:text-white"
            >
              <Mail size={12} className="inline mr-1 -mt-0.5" />
              or email us directly at contact@neurobolt.ai
            </a>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#94A3B8]">
        {label}
      </Label>
      {children}
    </div>
  );
}
